import { create } from 'zustand';

import { CURRENT_GUIDE } from '../config';
// import { GUIDE_STEPS_V2 } from '../config/steps';

type OnboardingStore = {
  isOpen: boolean;
  currentStep: number;
  totalSteps: number;
  /**
   * Current browser pathname, synced from inside the React Router tree via
   * OnboardingRouteSync. Used to compute isOnCorrectPage without requiring
   * useLocation() in the portal-based OnboardingProvider.
   */
  currentPathname: string;
  /** Open the guide, optionally at a specific step index (default: currentStep) */
  open: (step?: number) => void;
  /**
   * Pause: close the UI and advance to the next step.
   * The guide is NOT marked as seen — reopening via "Get Started" resumes from
   * the step after the one the user dismissed.
   */
  pause: () => void;
  close: () => void;
  next: () => void;
  prev: () => void;
  goTo: (step: number) => void;
  setPathname: (pathname: string) => void;
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  isOpen: false,
  currentStep: 0,
  totalSteps: CURRENT_GUIDE.length,
  currentPathname: window.location.pathname,

  open: (step) =>
    set((s) => ({ isOpen: true, currentStep: step ?? s.currentStep })),
  pause: () =>
    set((s) => ({
      isOpen: false,
      // Advance one step so reopening starts at the step after the dismissed one.
      // Clamp at the last step so we never go out of bounds.
      currentStep: Math.min(s.currentStep + 1, s.totalSteps - 1),
    })),
  close: () => set({ isOpen: false }),

  next: () => {
    const { currentStep, totalSteps } = get();
    if (currentStep < totalSteps - 1) set({ currentStep: currentStep + 1 });
  },

  prev: () => {
    const { currentStep } = get();
    if (currentStep > 0) set({ currentStep: currentStep - 1 });
  },

  goTo: (step) => set({ currentStep: step }),

  setPathname: (pathname) => set({ currentPathname: pathname }),
}));
